import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  fetchBriefingCurrent, generateBriefing, fetchBriefingHistory, emailMyBriefing,
} from "@/lib/api";
import { BRIEFING } from "@/constants/testIds";
import {
  BookOpen, RefreshCcw, AlertTriangle, AlertCircle, Trophy, XCircle,
  CalendarClock, MapPin, Users as UsersIcon, History, ArrowRight, Sparkles, ListChecks, Mail,
} from "lucide-react";

const PRIORITY_TINT = {
  Critical: "bg-[var(--fcc-red-soft)] text-[var(--fcc-red,#a23527)]",
  Important: "bg-[var(--fcc-peach)] text-[var(--fcc-ink)]",
  FYI: "bg-[var(--fcc-cream-2)] text-[var(--fcc-ink-soft)]",
};

const SEVERITY_TINT = {
  high: "bg-[var(--fcc-red-soft)] text-[var(--fcc-red,#a23527)]",
  medium: "bg-[var(--fcc-peach)] text-[var(--fcc-ink)]",
  low: "bg-[var(--fcc-cream-2)] text-[var(--fcc-ink-soft)]",
};

function fmtDate(s) {
  if (!s) return "";
  try {
    const d = new Date(s);
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  } catch { return s; }
}

export default function BriefingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const weekStartParam = searchParams.get("week_start");
  const [briefing, setBriefing] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = weekStartParam ? { week_start: weekStartParam } : {};
      const [cur, hist] = await Promise.all([fetchBriefingCurrent(params), fetchBriefingHistory()]);
      setBriefing(cur);
      setHistory(hist);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load this week's briefing.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, [weekStartParam]);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const fresh = await generateBriefing({ force: true, week_start: weekStartParam || undefined });
      setBriefing(fresh);
      const hist = await fetchBriefingHistory();
      setHistory(hist);
      toast.success("Weekly briefing regenerated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not regenerate.");
    } finally {
      setRegenerating(false);
    }
  };

  const [emailing, setEmailing] = useState(false);
  const handleEmailMe = async () => {
    setEmailing(true);
    try {
      const result = await emailMyBriefing();
      if (result?.status === "sent") {
        toast.success(`Briefing sent to ${result.to}.`);
      } else if (result?.status === "stubbed") {
        toast.success("Briefing rendered (email provider in stub mode — no real send).");
      } else if (result?.status === "skipped") {
        toast.warning("Email not sent — provider not configured. Add RESEND_API_KEY to enable.");
      } else {
        toast.success("Briefing emailed.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send email.");
    } finally {
      setEmailing(false);
    }
  };

  if (loading) {
    return (
      <div data-testid={BRIEFING.page} className="fcc-card p-10 sm:p-14 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[var(--fcc-green-soft)]">
          <RefreshCcw className="w-6 h-6 text-[var(--fcc-green)] animate-spin" />
        </div>
        <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-5">Building your weekly briefing…</h2>
        <p className="text-sm text-[var(--fcc-muted)] mt-2">Claude Sonnet 4.5 is reading the last 7 days and the next 7 days. Usually 15–25 seconds.</p>
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid={BRIEFING.page} className="fcc-card p-10 text-center">
        <AlertTriangle className="w-6 h-6 mx-auto text-[var(--fcc-orange)]" />
        <p className="mt-3 text-[var(--fcc-ink)] font-semibold">{error}</p>
        <button onClick={loadAll} className="fcc-btn-outline mt-5"><RefreshCcw className="w-4 h-4" /> Try again</button>
      </div>
    );
  }
  if (!briefing) return null;

  return (
    <div data-testid={BRIEFING.page} className="space-y-10">
      <HeaderCard
        briefing={briefing}
        onRegenerate={handleRegenerate}
        regenerating={regenerating}
        onEmailMe={handleEmailMe}
        emailing={emailing}
      />

      <div className="grid lg:grid-cols-3 gap-6">
        <RiskCard items={briefing.risk_flags} />
        <ConflictCard items={briefing.conflicts} />
        <ActionsCard items={briefing.action_items} />
      </div>

      <ChildSummaries items={briefing.child_summaries} />

      <CalendarOverview items={briefing.calendar_overview} />

      <div className="grid lg:grid-cols-3 gap-6">
        <WinsCard items={briefing.wins} />
        <MissedCard items={briefing.missed_items} />
        <DeadlinesCard items={briefing.upcoming_deadlines} />
      </div>

      <HistoryCard items={history.filter(h => h.id !== briefing.id)} />
    </div>
  );
}

function HeaderCard({ briefing, onRegenerate, regenerating, onEmailMe, emailing }) {
  const generatedFmt = briefing.generated_at
    ? new Date(briefing.generated_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })
    : "";
  return (
    <div className="fcc-card overflow-hidden">
      <div className="grid lg:grid-cols-12">
        <div className="lg:col-span-8 p-8 sm:p-10">
          <div className="flex flex-wrap gap-3 items-center">
            <span className="fcc-pill fcc-chip-emma"><BookOpen className="w-3 h-3" /> Weekly Command Center</span>
            <span className="fcc-pill bg-[var(--fcc-cream-2)] text-[var(--fcc-muted)]">
              {fmtDate(briefing.week_start)} → {fmtDate(briefing.week_end)}
            </span>
            <span className="fcc-pill bg-[var(--fcc-cream-2)] text-[var(--fcc-muted)]">Auto-generates Sundays 1 PM</span>
          </div>
          <h1
            data-testid={BRIEFING.headline}
            className="font-display text-[40px] sm:text-[52px] leading-[1.05] text-[var(--fcc-ink)] mt-6"
          >
            {briefing.family_headline}
          </h1>
          <p className="text-[var(--fcc-ink-soft)] mt-5 text-sm">Generated {generatedFmt} · {briefing.briefing_version}</p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              data-testid={BRIEFING.regenerateBtn}
              onClick={onRegenerate}
              disabled={regenerating}
              className="fcc-btn-primary disabled:opacity-50"
            >
              <RefreshCcw className={`w-4 h-4 ${regenerating ? "animate-spin" : ""}`} />
              {regenerating ? "Regenerating…" : "Regenerate weekly briefing"}
            </button>
            <button
              data-testid={BRIEFING.emailMeBtn}
              onClick={onEmailMe}
              disabled={emailing || regenerating}
              className="fcc-btn-outline disabled:opacity-50"
            >
              <Mail className={`w-4 h-4 ${emailing ? "animate-pulse" : ""}`} />
              {emailing ? "Sending…" : "Email me this briefing"}
            </button>
            <p className="text-xs text-[var(--fcc-muted)]">Regenerate usually takes 15–25 seconds.</p>
          </div>
        </div>
        <aside className="lg:col-span-4 p-7 sm:p-8 bg-[var(--fcc-cream-2)] border-l border-[var(--fcc-border)]">
          <p className="label-overline flex items-center gap-2"><Sparkles className="w-3 h-3" /> Top of mind</p>
          <ul className="mt-5 space-y-3">
            {(briefing.action_items || []).slice(0, 3).map((a, idx) => (
              <li key={idx} className="bg-white border border-[var(--fcc-border)] rounded-2xl p-4">
                <p className="font-semibold text-[var(--fcc-ink)] leading-snug">{a.title}</p>
                <p className="text-[11px] uppercase tracking-widest mt-1 text-[var(--fcc-muted)]">
                  {a.child_name || "Family"} · {a.priority || "FYI"}{a.due_date ? ` · ${fmtDate(a.due_date)}` : ""}
                </p>
              </li>
            ))}
            {(briefing.action_items || []).length === 0 && (
              <li className="text-sm text-[var(--fcc-muted)]">Nothing burning. Enjoy the week.</li>
            )}
          </ul>
        </aside>
      </div>
    </div>
  );
}

function RiskCard({ items = [] }) {
  return (
    <section className="fcc-card p-6">
      <h2 className="font-display text-2xl text-[var(--fcc-ink)] flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-[var(--fcc-orange)]" /> Risk flags
      </h2>
      <p className="text-xs text-[var(--fcc-muted)] mt-1">Things that could become a problem.</p>
      <ul className="mt-5 space-y-3">
        {items.length === 0 && <li className="text-sm text-[var(--fcc-muted)]">No risks detected.</li>}
        {items.map((r, i) => (
          <li key={i} data-testid={BRIEFING.riskRow} className="border border-[var(--fcc-border)] rounded-2xl p-4">
            <div className="flex items-start gap-2">
              <span className={`fcc-pill ${SEVERITY_TINT[r.severity] || SEVERITY_TINT.low}`}>{r.severity || "low"}</span>
              <p className="text-sm text-[var(--fcc-ink)] flex-1 leading-snug">{r.summary}</p>
            </div>
            <p className="text-[11px] uppercase tracking-widest mt-2 text-[var(--fcc-muted)]">
              {r.type?.replace(/_/g, " ") || "general"}{r.child_name ? ` · ${r.child_name}` : ""}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ConflictCard({ items = [] }) {
  return (
    <section className="fcc-card p-6">
      <h2 className="font-display text-2xl text-[var(--fcc-ink)] flex items-center gap-2">
        <AlertCircle className="w-5 h-5 text-[var(--fcc-orange)]" /> Schedule conflicts
      </h2>
      <p className="text-xs text-[var(--fcc-muted)] mt-1">Double-bookings & timing clashes.</p>
      <ul className="mt-5 space-y-3">
        {items.length === 0 && <li className="text-sm text-[var(--fcc-muted)]">No conflicts. Calendar is clean.</li>}
        {items.map((c, i) => (
          <li key={i} data-testid={BRIEFING.conflictRow} className="border border-[var(--fcc-border)] rounded-2xl p-4">
            <p className="text-xs label-overline">{fmtDate(c.date)}</p>
            <p className="text-sm text-[var(--fcc-ink)] mt-1 leading-snug">{c.summary}</p>
            {Array.isArray(c.items) && c.items.length > 0 && (
              <ul className="mt-2 text-xs text-[var(--fcc-muted)] list-disc ml-4 space-y-1">
                {c.items.map((it, j) => <li key={j}>{it}</li>)}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function ActionsCard({ items = [] }) {
  return (
    <section className="fcc-card p-6">
      <h2 className="font-display text-2xl text-[var(--fcc-ink)] flex items-center gap-2">
        <ListChecks className="w-5 h-5 text-[var(--fcc-green)]" /> Parent action items
      </h2>
      <p className="text-xs text-[var(--fcc-muted)] mt-1">Sorted by priority.</p>
      <ul className="mt-5 space-y-3">
        {items.length === 0 && <li className="text-sm text-[var(--fcc-muted)]">Nothing burning this week.</li>}
        {items.map((a, i) => (
          <li key={i} data-testid={BRIEFING.actionRow} className="border border-[var(--fcc-border)] rounded-2xl p-4">
            <div className="flex items-start gap-2">
              <span className={`fcc-pill ${PRIORITY_TINT[a.priority] || PRIORITY_TINT.FYI}`}>{a.priority || "FYI"}</span>
              <p className="text-sm text-[var(--fcc-ink)] flex-1 leading-snug font-semibold">{a.title}</p>
            </div>
            <p className="text-[11px] uppercase tracking-widest mt-2 text-[var(--fcc-muted)]">
              {a.child_name || "Family"}{a.due_date ? ` · due ${fmtDate(a.due_date)}` : ""}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ChildSummaries({ items = [] }) {
  if (items.length === 0) return null;
  return (
    <section>
      <h2 className="font-display text-3xl text-[var(--fcc-ink)] mb-5 flex items-center gap-2">
        <UsersIcon className="w-6 h-6 text-[var(--fcc-green)]" /> Per-child summary
      </h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((c, i) => (
          <article key={i} data-testid={BRIEFING.childCard} className="fcc-card p-6">
            <h3 className="font-display text-2xl text-[var(--fcc-ink)]">{c.child_name || "Child"}</h3>
            {c.summary && <p className="text-sm text-[var(--fcc-ink-soft)] mt-2 leading-relaxed">{c.summary}</p>}
            <Bullets label="Wins" items={c.wins} icon={<Trophy className="w-3.5 h-3.5" />} />
            <Bullets label="Misses" items={c.misses} icon={<XCircle className="w-3.5 h-3.5" />} />
            <Bullets label="Upcoming" items={c.upcoming} icon={<CalendarClock className="w-3.5 h-3.5" />} />
          </article>
        ))}
      </div>
    </section>
  );
}

function Bullets({ label, items, icon }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-4">
      <p className="label-overline flex items-center gap-1.5">{icon} {label}</p>
      <ul className="mt-2 space-y-1.5">
        {items.map((it, j) => (
          <li key={j} className="text-sm text-[var(--fcc-ink-soft)] leading-snug">• {it}</li>
        ))}
      </ul>
    </div>
  );
}

function CalendarOverview({ items = [] }) {
  if (items.length === 0) return null;
  // Group by date
  const byDate = items.reduce((acc, ev) => {
    const d = ev.date || "Unknown";
    (acc[d] = acc[d] || []).push(ev);
    return acc;
  }, {});
  const sortedDates = Object.keys(byDate).sort();
  return (
    <section className="fcc-card p-6 sm:p-8">
      <h2 className="font-display text-3xl text-[var(--fcc-ink)] mb-5 flex items-center gap-2">
        <CalendarClock className="w-6 h-6 text-[var(--fcc-green)]" /> Calendar overview
      </h2>
      <div className="space-y-5">
        {sortedDates.map(d => (
          <div key={d}>
            <p className="label-overline">{fmtDate(d)}</p>
            <ul className="mt-2 space-y-2">
              {byDate[d].map((ev, i) => (
                <li
                  key={i}
                  data-testid={BRIEFING.calendarRow}
                  className="border border-[var(--fcc-border)] rounded-xl p-3 flex flex-wrap items-center gap-3"
                >
                  <span className="fcc-pill bg-[var(--fcc-cream-2)] text-[var(--fcc-ink)]">{ev.child_name || "Family"}</span>
                  <span className="text-sm font-semibold text-[var(--fcc-ink)] flex-1 min-w-[140px]">{ev.title}</span>
                  {ev.time && <span className="text-xs text-[var(--fcc-muted)]">{ev.time}</span>}
                  {ev.location && (
                    <span className="text-xs text-[var(--fcc-muted)] flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {ev.location}
                    </span>
                  )}
                  {ev.category && <span className="fcc-pill bg-[var(--fcc-cream-2)] text-[var(--fcc-muted)]">{ev.category}</span>}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

function WinsCard({ items = [] }) {
  return (
    <section className="fcc-card p-6">
      <h2 className="font-display text-xl text-[var(--fcc-ink)] flex items-center gap-2">
        <Trophy className="w-5 h-5 text-[var(--fcc-green)]" /> Weekly wins
      </h2>
      <ul data-testid={BRIEFING.winsList} className="mt-4 space-y-2">
        {items.length === 0 && <li className="text-sm text-[var(--fcc-muted)]">No wins logged yet.</li>}
        {items.map((w, i) => (
          <li key={i} className="text-sm text-[var(--fcc-ink-soft)] leading-snug">✓ {w}</li>
        ))}
      </ul>
    </section>
  );
}

function MissedCard({ items = [] }) {
  return (
    <section className="fcc-card p-6">
      <h2 className="font-display text-xl text-[var(--fcc-ink)] flex items-center gap-2">
        <XCircle className="w-5 h-5 text-[var(--fcc-orange)]" /> Missed / overdue
      </h2>
      <ul data-testid={BRIEFING.missedList} className="mt-4 space-y-2">
        {items.length === 0 && <li className="text-sm text-[var(--fcc-muted)]">Nothing slipped this week.</li>}
        {items.map((m, i) => (
          <li key={i} className="text-sm text-[var(--fcc-ink-soft)] leading-snug">✗ {m}</li>
        ))}
      </ul>
    </section>
  );
}

function DeadlinesCard({ items = [] }) {
  return (
    <section className="fcc-card p-6">
      <h2 className="font-display text-xl text-[var(--fcc-ink)] flex items-center gap-2">
        <CalendarClock className="w-5 h-5 text-[var(--fcc-green)]" /> Upcoming deadlines
      </h2>
      <ul className="mt-4 space-y-3">
        {items.length === 0 && <li className="text-sm text-[var(--fcc-muted)]">No deadlines on the radar.</li>}
        {items.map((d, i) => (
          <li key={i} data-testid={BRIEFING.deadlineRow} className="border border-[var(--fcc-border)] rounded-xl p-3">
            <p className="text-sm font-semibold text-[var(--fcc-ink)]">{d.title}</p>
            <p className="text-[11px] uppercase tracking-widest mt-1 text-[var(--fcc-muted)]">
              {d.child_name || "Family"}{d.due_date ? ` · ${fmtDate(d.due_date)}` : ""}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function HistoryCard({ items = [] }) {
  const [, setSearchParams] = useSearchParams();
  if (items.length === 0) return null;
  return (
    <section className="fcc-card p-6 sm:p-8">
      <h2 className="font-display text-2xl text-[var(--fcc-ink)] mb-4 flex items-center gap-2">
        <History className="w-5 h-5 text-[var(--fcc-muted)]" /> Past briefings
      </h2>
      <ul className="space-y-2">
        {items.map((h) => (
          <li
            key={h.id}
            data-testid={BRIEFING.historyRow}
            className="border border-[var(--fcc-border)] rounded-xl p-3 flex items-center gap-3"
          >
            <span className="fcc-pill bg-[var(--fcc-cream-2)] text-[var(--fcc-muted)]">
              {fmtDate(h.week_start)} → {fmtDate(h.week_end)}
            </span>
            <span className="text-sm text-[var(--fcc-ink-soft)] flex-1 truncate">{h.family_headline}</span>
            <button
              type="button"
              onClick={() => setSearchParams({ week_start: h.week_start })}
              className="text-xs text-[var(--fcc-green)] hover:underline flex items-center gap-1"
            >
              View <ArrowRight className="w-3 h-3" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
